import {
    PRODUCT_GROUP_TYPE,
    DealLineItem,
    PRODUCT_GROUP_TYPES,
    PRODUCT_CATEGORIES,
    PRODUCT_CATEGORY,
    PRODUCT_TYPES,
    PRICING_STRATEGIES,
} from '@schuettflix/interfaces';
import { matchPricingStrategyByDealPricesAndProductType } from './matchPricingStrategyByDealPricesAndProductType';
import { getCompatiblePricingStrategies } from './getCompatiblePricingStrategies';
import {
    getCompatibleProductTypes,
    getProductCategoryByProductType,
    getProductTypesByProductCategory,
} from '@schuettflix/product-group-utils';

type DealLineItemId = DealLineItem['salesforceMetaData']['opportunityLineItemId'];

type AvailableDealLineItem = {
    salesforceMetaData: Pick<DealLineItem['salesforceMetaData'], 'opportunityLineItemId'>;
    product: Pick<DealLineItem['product'], 'productType'>;
} & Pick<DealLineItem, 'materialPrice' | 'transportPrice' | 'francoPrice'>;

function getDealLineItemsWithPricingStrategyAndProductType(availableDealLineItems: AvailableDealLineItem[]) {
    return availableDealLineItems.flatMap(dealLineItem => {
        if (!dealLineItem.product.productType) {
            return [];
        }

        const matchedPricingStrategy = matchPricingStrategyByDealPricesAndProductType(
            dealLineItem,
            dealLineItem.product.productType
        );
        return matchedPricingStrategy
            ? {
                  ...dealLineItem,
                  product: {
                      ...dealLineItem.product,
                      productType: dealLineItem.product.productType,
                  },
                  pricingStrategy: matchedPricingStrategy,
              }
            : [];
    });
}

function getdealLineItemsWithCompatiblePricingStrategies(
    selectedDealLineItemIds: DealLineItemId[],
    selectedProductCategoriesSet: Set<PRODUCT_CATEGORY>,
    dealLineItemsWithPricingStrategy: ReturnType<typeof getDealLineItemsWithPricingStrategyAndProductType>
) {
    const selectedPricingStrategies = selectedDealLineItemIds.flatMap(selectedDealLineItemId => {
        const selectedDealLineItem = dealLineItemsWithPricingStrategy.find(
            ({ salesforceMetaData }) => salesforceMetaData.opportunityLineItemId === selectedDealLineItemId
        );
        return selectedDealLineItem ? [selectedDealLineItem.pricingStrategy] : [];
    });

    const { mainPricingStrategy, compatiblePricingStrategies } =
        getCompatiblePricingStrategies(selectedPricingStrategies);
    const compatiblePricingStrategiesSet = new Set(compatiblePricingStrategies);

    if (
        mainPricingStrategy === PRODUCT_GROUP_TYPES.STANDARD &&
        (selectedProductCategoriesSet.has(PRODUCT_CATEGORIES.TRANSPORT) ||
            selectedProductCategoriesSet.has(PRODUCT_CATEGORIES.MATERIAL))
    ) {
        compatiblePricingStrategiesSet.delete(PRODUCT_GROUP_TYPES.FRANCO);
        compatiblePricingStrategiesSet.delete(PRODUCT_GROUP_TYPES.MERCHANT);
    }

    return {
        mainPricingStrategy,
        dealLineItemsWithCompatiblePricingStrategies: dealLineItemsWithPricingStrategy.filter(({ pricingStrategy }) =>
            compatiblePricingStrategiesSet.has(pricingStrategy)
        ),
    };
}

function getCompatibleDealLineItemsBy(
    mainPricingStrategy: PRODUCT_GROUP_TYPE,
    compatibleProductTypes: PRODUCT_TYPES[],
    selectedProductCategoriesSet: Set<PRODUCT_CATEGORY>,
    dealLineItemsWithCompatiblePricingStrategies: ReturnType<typeof getDealLineItemsWithPricingStrategyAndProductType>
) {
    const compatibleProductTypesSet = new Set(compatibleProductTypes);

    // In case of "STANDARD" there are various scenarios to take case
    if (mainPricingStrategy === PRODUCT_GROUP_TYPES.STANDARD) {
        const compatibleProductCategories = [...compatibleProductTypesSet].map(productType =>
            getProductCategoryByProductType(productType)
        );

        // If a material product was selected a transport is also compatible selected
        if (compatibleProductCategories.includes(PRODUCT_CATEGORIES.MATERIAL)) {
            for (const productType of getProductTypesByProductCategory(PRODUCT_CATEGORIES.TRANSPORT)) {
                compatibleProductTypesSet.add(productType);
            }
            // If no material product was selected but a transport than all material products are compatible
        } else if (compatibleProductCategories.includes(PRODUCT_CATEGORIES.TRANSPORT)) {
            for (const productType of getProductTypesByProductCategory(PRODUCT_CATEGORIES.MATERIAL)) {
                compatibleProductTypesSet.add(productType);
            }
            // If no material and transport product was selected there are two other scenarios
        } else {
            // If there is a material service selected all material products are compatible
            if (selectedProductCategoriesSet.has(PRODUCT_CATEGORIES.MATERIAL_SERVICE)) {
                for (const productType of getProductTypesByProductCategory(PRODUCT_CATEGORIES.MATERIAL)) {
                    compatibleProductTypesSet.add(productType);
                }
            }
            // If there is a transport service selected all transport products are compatible
            if (selectedProductCategoriesSet.has(PRODUCT_CATEGORIES.TRANSPORT_SERVICE)) {
                for (const productType of getProductTypesByProductCategory(PRODUCT_CATEGORIES.TRANSPORT)) {
                    compatibleProductTypesSet.add(productType);
                }
            }
        }
    }

    const compatibleDealLineItems = dealLineItemsWithCompatiblePricingStrategies.filter(
        ({ product, pricingStrategy }) =>
            // Checks if the line items product type is listed as a compatible product type
            compatibleProductTypesSet.has(product.productType) ||
            // In case there is a transport service selected and a merchant/franco case is in the list of compatible pricing strategys
            // then the merchant and franco cases are also compatible
            (pricingStrategy !== PRODUCT_GROUP_TYPES.STANDARD &&
                selectedProductCategoriesSet.has(PRODUCT_CATEGORIES.TRANSPORT_SERVICE))
    );

    return compatibleDealLineItems;
}

export function getCompatibleDealLineItems({
    availableDealLineItems,
    selectedDealLineItemIds,
}: {
    availableDealLineItems: AvailableDealLineItem[];
    selectedDealLineItemIds: DealLineItemId[];
}): {
    isValidSelection: boolean;
    mainPricingStrategy: PRODUCT_GROUP_TYPE | null;
    compatibleDealLineItemIds: DealLineItemId[];
} {
    const dealLineItemsWithPricingStrategy = getDealLineItemsWithPricingStrategyAndProductType(availableDealLineItems);

    if (selectedDealLineItemIds.length === 0) {
        return {
            isValidSelection: true,
            mainPricingStrategy: PRICING_STRATEGIES.STANDARD,
            compatibleDealLineItemIds: dealLineItemsWithPricingStrategy.map(
                ({ salesforceMetaData }) => salesforceMetaData.opportunityLineItemId
            ),
        };
    }

    const selectedProductTypes = selectedDealLineItemIds.flatMap(
        selectedDealLineItemId =>
            dealLineItemsWithPricingStrategy.find(
                ({ salesforceMetaData }) => salesforceMetaData.opportunityLineItemId === selectedDealLineItemId
            )?.product.productType ?? []
    );
    const selectedProductCategoriesSet = new Set(
        selectedProductTypes.map(productType => getProductCategoryByProductType(productType))
    );

    // Filters the list to deal line items that have a pricing strategy that is compatible to the current selection
    const { mainPricingStrategy, dealLineItemsWithCompatiblePricingStrategies } =
        getdealLineItemsWithCompatiblePricingStrategies(
            selectedDealLineItemIds,
            selectedProductCategoriesSet,
            dealLineItemsWithPricingStrategy
        );

    if (!mainPricingStrategy) {
        return {
            isValidSelection: false,
            mainPricingStrategy: null,
            compatibleDealLineItemIds: [],
        };
    }

    // If there is a franco or merchant case a custom transport product is implicitly selected therefore it has explicitly be added to the selected product types list
    if (mainPricingStrategy !== PRODUCT_GROUP_TYPES.STANDARD && !selectedProductTypes.includes('CUSTOM_TRANSPORT')) {
        selectedProductTypes.push('CUSTOM_TRANSPORT');
    }

    const { isValidSelection, compatibleProductTypes } = getCompatibleProductTypes({
        selectedProductGroupType: mainPricingStrategy,
        selectedProductTypes,
    });
    if (!isValidSelection) {
        return {
            isValidSelection: false,
            mainPricingStrategy: null,
            compatibleDealLineItemIds: [],
        };
    }

    // Filters the list to deal line items that have a product type that is compatible to the current selection
    const compatibleDealLineItems = getCompatibleDealLineItemsBy(
        mainPricingStrategy,
        compatibleProductTypes,
        selectedProductCategoriesSet,
        dealLineItemsWithCompatiblePricingStrategies
    );

    const compatibleDealLineItemIds = [
        ...new Set([
            ...compatibleDealLineItems.map(({ salesforceMetaData }) => salesforceMetaData.opportunityLineItemId),
            ...selectedDealLineItemIds,
        ]),
    ];

    return {
        isValidSelection: true,
        mainPricingStrategy,
        compatibleDealLineItemIds,
    };
}
